import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Eye, Phone, Mail, Globe, Share2, Image as ImageIcon, Sparkles, Info, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type Stats = {
  views: number;
  shares: number;
  contactClicks: number;
  graphicDownloads: number;
  contactBreakdown: { phone: number; email: number; website: number };
};

type RangeKey = "today" | "30d";

const RANGE_DAYS: Record<RangeKey, number> = {
  today: 1,
  "30d": 30,
};

const RANGE_LABEL: Record<RangeKey, string> = {
  today: "Aktywność profilu z dzisiaj",
  "30d": "Aktywność profilu z ostatnich 30 dni",
};

const fetchStats = async (shelterId: string, days: number): Promise<Stats> => {
  // Używamy SECURITY DEFINER RPC, bo właściciel nie ma dostępu SELECT do
  // tabel analitycznych (page_visits / analytics_events / graphic_downloads).
  const { data, error } = await supabase.rpc("get_owner_shelter_stats", {
    _shelter_id: shelterId,
    _days: days,
  });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, number>;
  const phone = Number(d.phone ?? 0);
  const email = Number(d.email ?? 0);
  const website = Number(d.website ?? 0);
  return {
    views: Number(d.views ?? 0),
    shares: Number(d.shares ?? 0),
    contactClicks: phone + email + website,
    graphicDownloads: Number(d.downloads ?? 0),
    contactBreakdown: { phone, email, website },
  };
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  emptyText,
  tooltip,
}: {
  icon: any;
  label: string;
  value: number;
  emptyText: string;
  tooltip?: string;
}) => (
  <Card>
    <CardContent className="p-2.5 sm:p-4">
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground mb-1.5 sm:mb-2 leading-tight">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
        {tooltip && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center text-muted-foreground/70 hover:text-foreground transition-colors shrink-0"
                  aria-label="Co liczy ten wskaźnik"
                >
                  <Info className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {value > 0 ? (
        <div className="text-xl sm:text-2xl font-semibold leading-none">{value}</div>
      ) : (
        <div className="text-[11px] sm:text-xs text-muted-foreground/80 leading-snug">{emptyText}</div>
      )}
    </CardContent>
  </Card>
);

const Statistics = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: owned } = useOwnedShelters(user?.id);

  const shelter = owned?.find((s) => s.slug === slug);

  useEffect(() => {
    if (owned && !shelter && slug) {
      // jeśli nie posiada schroniska -> wracamy na pulpit
      navigate("/panel", { replace: true });
    }
  }, [owned, shelter, slug, navigate]);

  const [range, setRange] = useState<RangeKey>("30d");
  const [copied, setCopied] = useState(false);

  const shareUrl = shelter ? `https://gdzieschronisko.pl/schronisko/${shelter.slug}?utm_source=share` : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link skopiowany");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować linku");
    }
  };

  const { data: stats, isLoading } = useQuery({
    queryKey: ["panel-stats", shelter?.id, shelter?.slug, range],
    enabled: !!shelter?.id && !!shelter?.slug,
    queryFn: () => fetchStats(shelter!.id, RANGE_DAYS[range]),
  });

  if (!shelter) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalActivity = stats
    ? stats.views + stats.shares + stats.contactClicks + stats.graphicDownloads
    : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-2.5 sm:space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <h1 className="text-base sm:text-2xl font-bold tracking-tight truncate">
            <span className="lg:hidden">Statystyki</span>
            <span className="hidden lg:inline">Statystyki - {shelter.name}</span>
          </h1>
          <p className="text-[11px] sm:text-sm text-muted-foreground">{RANGE_LABEL[range]}</p>
        </div>
        <div
          role="tablist"
          aria-label="Zakres czasu"
          className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5 text-xs"
        >
          {(["today", "30d"] as RangeKey[]).map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={range === key}
              onClick={() => setRange(key)}
              className={cn(
                "px-3 py-1.5 rounded-md font-medium transition-colors",
                range === key
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {key === "today" ? "Dzisiaj" : "Ostatnie 30 dni"}
            </button>
          ))}
        </div>
      </div>

      {isLoading || !stats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {totalActivity === 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/40 p-3">
              <Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <p className="text-sm text-muted-foreground">
                {range === "today"
                  ? "Brak aktywności w dniu dzisiejszym. Sprawdź zakres ostatnich 30 dni."
                  : "Profil jest już aktywny. Statystyki pojawią się, gdy użytkownicy zaczną odwiedzać profil schroniska."}
              </p>
            </div>
          )}

          <div className="grid gap-2 sm:gap-3 grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Eye}
              label="Wyświetlenia"
              value={stats.views}
              emptyText="Brak wejść"
              tooltip="Suma kliknięć w kafelek schroniska (na mapie i liście) oraz wejść na pełną stronę schroniska na gdzieschronisko.pl. Nie obejmuje wejść na Waszą własną stronę www."
            />
            <StatCard
              icon={Share2}
              label="Udostępnienia"
              value={stats.shares}
              emptyText="Brak"
              tooltip="Liczba wejść na profil schroniska z udostępnionego linku (np. wklejonego na Facebooku, Messengerze, w mailu)."
            />
            <StatCard
              icon={Phone}
              label="Kontakt"
              value={stats.contactClicks}
              emptyText="Brak kliknięć"
              tooltip="Suma kliknięć w numer telefonu, adres email i link do strony www schroniska. Rozbicie na poszczególne kanały znajdziesz niżej."
            />
            <StatCard
              icon={ImageIcon}
              label="Grafiki"
              value={stats.graphicDownloads}
              emptyText="Brak pobrań"
              tooltip="Liczba pobrań gotowych grafik schroniska przez użytkowników odwiedzających profil (np. przez przycisk Udostępnij). Nie obejmuje pobrań z panelu administratora."
            />
          </div>

          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                <Share2 className="h-3.5 w-3.5" />
                <span>Link do profilu schroniska</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded-md bg-muted/60 px-2.5 py-1.5 text-xs font-mono text-foreground/80">
                  {shareUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="shrink-0 gap-1.5"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Skopiowano" : "Kopiuj"}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground/80">
                To jest link, który warto udostępniać na Facebooku, Messengerze czy w mailu - jego wejścia liczone są jako "Udostępnienia".
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Rozbicie kliknięć</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
              <ul className="divide-y divide-border/50">
                {[
                  { icon: Phone, label: "Telefon", value: stats.contactBreakdown.phone },
                  { icon: Mail, label: "Email", value: stats.contactBreakdown.email },
                  { icon: Globe, label: "Strona www", value: stats.contactBreakdown.website },
                ].map((row) => (
                  <li key={row.label} className="flex items-center justify-between py-2 text-sm">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <row.icon className="h-3.5 w-3.5" /> {row.label}
                    </span>
                    <span className={row.value > 0 ? "font-medium" : "text-muted-foreground/70"}>
                      {row.value}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Statistics;
